/**
 * Navbar notifications bell and dropdown panel.
 */
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationsStream } from '@/hooks/useNotificationsStream';
import { notificationsApi } from '@/services/notifications.service';
import type { AppNotification, PaginatedNotifications } from '@/types/notification.types';
import { NotificationsPanel } from '@/components/notifications/NotificationsPanel';
import {
  NOTIFICATION_LIST_PAGE_SIZE,
  NOTIFICATION_QUERY_KEYS,
} from '@/components/notifications/notification-configs';
import { resolveDeepLink } from '@/components/notifications/notification-utils';
import { NotificationBadgeCount } from '@/components/notifications/NotificationBadgeCount';
/** Navbar bell: unread badge, SSE/push, and the notifications dropdown panel. */
export const NotificationsBell: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [open, setOpen] = React.useState(false);

  useNotificationsStream(isAuthenticated);
  usePushNotifications(isAuthenticated);

  const countQuery = useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.unreadCount,
    queryFn: notificationsApi.unreadCount,
    enabled: isAuthenticated,
    refetchOnWindowFocus: true,
  });

  const listQuery = useInfiniteQuery<PaginatedNotifications>({
    queryKey: NOTIFICATION_QUERY_KEYS.infinite(NOTIFICATION_LIST_PAGE_SIZE, false),
    queryFn: ({ pageParam }) =>
      notificationsApi.list(pageParam as number, NOTIFICATION_LIST_PAGE_SIZE, false),
    initialPageParam: 1,
    getNextPageParam: (last) => {
      const shown = last.page * last.pageSize;
      return shown < last.total ? last.page + 1 : undefined;
    },
    enabled: open && isAuthenticated,
  });

  /** Refresh the list and unread-count queries after a mark-read mutation. */
  const invalidateNotifications = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.all });
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.unreadCount });
  }, [queryClient]);

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: invalidateNotifications,
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: invalidateNotifications,
  });

  const unreadCount = countQuery.data ?? 0;

  /** Flatten infinite-query pages and drop duplicate notification ids. */
  const items = React.useMemo(() => {
    const pages = listQuery.data?.pages;
    if (!pages?.length) return [] as AppNotification[];
    const seen = new Set<string>();
    const out: AppNotification[] = [];
    for (const page of pages) {
      for (const n of page.items) {
        if (seen.has(n.id)) continue;
        seen.add(n.id);
        out.push(n);
      }
    }
    return out;
  }, [listQuery.data]);

  const total = listQuery.data?.pages?.[0]?.total ?? items.length;

  /** Fetch the next page when the panel reports it has more items. */
  const handleLoadMore = React.useCallback(() => {
    if (!listQuery.hasNextPage || listQuery.isFetchingNextPage) return;
    void listQuery.fetchNextPage();
  }, [listQuery]);

  /** Mark unread items read, then follow the notification deep-link if any. */
  const handleClick = (n: AppNotification) => {
    if (!n.readAt) markReadMutation.mutate(n.id);
    const to = resolveDeepLink(n);
    if (to) {
      void navigate(to);
      setOpen(false);
    }
  };

  function handleClose(): void {
    setOpen(false);
  }

  function handleMarkAllRead(): void {
    markAllReadMutation.mutate();
  }

  function renderBackdrop(): React.ReactNode {
    if (!open || typeof document === 'undefined') return null;
    return createPortal(
      <div className="notifications-backdrop" aria-hidden onClick={handleClose} />,
      document.body,
    );
  }

  return (
    <>
      {renderBackdrop()}
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  className="navbar-icon-button hidden sm:flex relative"
                  aria-label={
                    unreadCount > 0 ? `Notifications (${unreadCount} non lues)` : 'Notifications'
                  }
                  type="button"
                >
                  <Bell />
                  {unreadCount > 0 && (
                    <span className="navbar-notification-badge-count" aria-hidden="true">
                      <NotificationBadgeCount count={unreadCount} />
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>Notifications</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <DropdownMenuContent
          align="end"
          sideOffset={10}
          className="notifications-dropdown-content w-[420px] max-w-[calc(100vw-1rem)] p-0 !origin-top-right border-0 shadow-none bg-transparent"
        >
          <NotificationsPanel
            items={items}
            total={total}
            unreadCount={unreadCount}
            isLoading={listQuery.isLoading}
            isFetchingMore={listQuery.isFetchingNextPage}
            hasMore={listQuery.hasNextPage ?? false}
            onLoadMore={handleLoadMore}
            isMarkingAllRead={markAllReadMutation.isPending}
            onItemClick={handleClick}
            onMarkAllRead={handleMarkAllRead}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
